import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import {
  AtlasConfiguration,
  Commune,
  LandingPageItem,
} from '../../../models/commune';
import { TopicStore } from '../../../stores/topic/topic.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { PickList, PickListModule } from 'primeng/picklist';
import { PaginatorModule } from 'primeng/paginator';
import { StoryStore } from '../../../stores/story/story.state';
import { Story } from '../../../models/story';
import { Bookmark } from '../../../models/bookmark';
import { Topic } from '../../../models/topic';
import { DividerModule } from 'primeng/divider';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { InputTextModule } from 'primeng/inputtext';

import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
import { entityTypeLabels } from '../../../utility/entity-type-label';
@Component({
  selector: 'app-configuration-landingpage',
  standalone: true,
  imports: [
    InputTextModule,
    PickListModule,
    PaginatorModule,
    ReactiveFormsModule,
    DividerModule,
    FormFieldWrapperComponent,
    HeaderComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './configuration-landingpage.component.html',
  styleUrl: './configuration-landingpage.component.scss',
})
export class ConfigurationLandingpageComponent implements OnInit {
  @ViewChild('listPickerCtrl') listPickerCtrl!: PickList;
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly topicStore = inject(TopicStore);

  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  private readonly destroyRef = inject(DestroyRef);
  protected readonly typeLabels = entityTypeLabels;
  activeCommune: Commune | null = null;
  atlasConfiguration?: AtlasConfiguration;
  availableItems: LandingPageItem[] = [];
  selection: LandingPageItem[] = [];

  constructor(private fb: FormBuilder) {
    effect(() => {
      if (!this.activeCommune) {
        this.activeCommune = this.globalStore.activeCommune();
        this.atlasConfiguration = this.activeCommune?.atlasConfig;
      }
      this.init();
    });
  }

  /**
   * Initializes the component by invoking the initForm method.
   * This method is called automatically when the component is initialized.
   *
   * @return {void} This method does not return any value.
   */
  ngOnInit(): void {
    this.initForm();
  }

  /**
   * Initializes the necessary configurations and setups for the application.
   * Executes methods to set the selection, define available items, and initialize form elements.
   *
   * @return {void} Does not return a value.
   */
  init(): void {
    this.setSelection();
    this.setAvailableItems();
    this.initForm();
  }

  /**
   * Sets the `selection` property based on the active commune's landing page items.
   * It maps the items to their respective representations depending on their type
   * ('Topic', 'Bookmark', or 'Story'). If no selection is available, it initializes
   * `selection` to an empty array.
   *
   * @return {void} No return value.
   */
  setSelection(): void {
    const selection: LandingPageItem[] | undefined =
      this.activeCommune?.atlasConfig?.landingPageItems;
    if (!selection) {
      this.selection = [];
      return;
    }
    this.selection = selection
      .map((item: LandingPageItem) => {
        switch (item.type) {
          case 'Topic':
            return this.getTopicItem(this.topicStore.getById(item.id));
          case 'Bookmark':
            return this.getBookmarkItem(this.bookmarkStore.getById(item.id));
          case 'Story':
            return this.getStoryItem(this.storyStore.getById(item.id));
        }
        return item;
      })
      .filter(item => !!item) as LandingPageItem[];
  }

  /**
   * Combines filtered and sorted lists of topics, bookmarks, and stories into a single collection of available items.
   * Excludes items that are already selected based on their IDs.
   * Updates the `availableItems` property with the resulting list.
   *
   * @return {void} This method does not return a value.
   */
  setAvailableItems(): void {
    const selectedIds = this.selection.map(selected => selected.id);

    const topics =
      this.topicStore
        .filteredSorted()
        ?.map(topic => this.getTopicItem(topic))
        .filter((item): item is LandingPageItem => !!item) ?? [];

    const bookmarks =
      this.bookmarkStore
        .filteredSorted()
        ?.map(bookmark => this.getBookmarkItem(bookmark))
        .filter((item): item is LandingPageItem => !!item) ?? [];

    const stories =
      this.storyStore
        .filteredSorted()
        ?.map(story => this.getStoryItem(story))
        .filter((item): item is LandingPageItem => !!item) ?? [];

    this.availableItems = [...topics, ...bookmarks, ...stories].filter(
      item => !selectedIds.includes(item.id)
    );
  }

  /**
   * Transforms a Bookmark object into a standardized bookmark item format.
   *
   * @param {Bookmark} bookmark - The Bookmark object containing details to be transformed.
   * @return {Object} An object representing the formatted bookmark item, including `id`, `icon`, `name`, `text`, and `type`.
   */
  getBookmarkItem(bookmark?: Bookmark): LandingPageItem | undefined {
    if (!bookmark) return;
    return {
      id: bookmark._id,
      icon: 'fas fa-note-sticky',
      name: bookmark.title,
      text: bookmark.headline || '',
      type: 'Bookmark',
    };
  }

  /**
   * Constructs and returns an object representing a story item based on the given story.
   *
   * @param {Story} story - The story object containing details to generate the story item.
   * @return {Object} An object containing the story item's id, icon, name, text, and type.
   */
  getStoryItem(story?: Story): LandingPageItem | undefined {
    if (!story) return;
    return {
      id: story._id,
      icon: 'fas fa-file-lines',
      name: story.title,
      text: story.description || '',
      type: 'Story',
    };
  }

  /**
   * generate topic-landingpage-item by topic
   * @param topic
   */
  getTopicItem(topic?: Topic): LandingPageItem | undefined {
    if (!topic) return;
    return {
      id: topic._id,
      name: topic.title,
      text: topic.title,
      type: 'Topic',
      icon: `fas fa-${topic.icon}`,
    };
  }

  /**
   * Initializes the form with default values sourced from the atlasConfiguration object.
   * Sets up reactive form controls for handling user input and binds to value changes to automatically trigger submission.
   *
   * @return {void} No return value.
   */
  initForm(): void {
    this.form = this.fb.group({
      headline: [this.atlasConfiguration?.headline || ''],
      startTeaser: [this.atlasConfiguration?.startTeaser || ''],
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.submit();
      });
  }

  /**
   * Submits the current form data and updates the atlas configuration of the active commune.
   *
   * This method merges the values from the form into the `bar` property
   * of the `atlasConfig` object of the active commune and persists the updated commune data
   * to the commune store.
   *
   * @return {void} Does not return any value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    // copy: the store item must not change before the update succeeded.
    // landingPageItems are not part of the form, they are saved by the pick list.
    this.activeCommune = {
      ...this.activeCommune,
      atlasConfig: {
        ...this.activeCommune.atlasConfig,
        ...this.form.value,
      },
    };
    this.communeStore.update(this.activeCommune);
  }

  /**
   * Saves the current selection to the active commune's atlas configuration.
   * Updates the landing page items in the atlas configuration with the current selection
   * and applies the changes to the commune store.
   *
   * @return {void} This method does not return a value.
   */
  saveToAtlasConfig(): void {
    if (!this.activeCommune) return;
    // copy: the store item must not change before the update succeeded
    this.activeCommune = {
      ...this.activeCommune,
      atlasConfig: {
        ...this.activeCommune.atlasConfig,
        landingPageItems: [...this.selection],
      },
    };
    this.communeStore.update(this.activeCommune);
  }
}
